import request from 'sb-request'
import actions from 'redux/actions'
import { getState } from 'redux/core'
import { getFeatureToggle, getCookieValue } from 'helpers'
import { constants, mixpanel, getPrefixedAddressFields, links, sentry } from 'common/helpers'
import { ShippingType, CreditCardType } from 'pages/paymentNew/SubscribePage/util/forms'

import amazonUserProfileQuery from './amazonUserProfileQuery.graphql'

import {
  setupBegin, setupCommit, setupBeginPayPal, setupCommitPayPal, callVindicia, subscriptionProcessing,
  syncUserAndSubscription, setupBeginApplePay, setupCommitApplePay,
  callAnalytics, checkRequiredFields,
} from './util'


const removeSubscriptionCase = () => {
  const { models: { payment: { subscriptionCase } } } = getState()

  if (subscriptionCase) {
    actions.models.payment.setSubscriptionCaseStatus(false)
  }
}

const getNextPageUrl = (gender) => {
  let url = links.thankYou
  const mainPageThxNotification = getCookieValue(constants.cookieNames.mainPageThxNotification)

  if (mainPageThxNotification) {
    url = gender === 'male' ? links.subscription.mainMen : links.subscription.mainWomen
    url = `${url}?notification=${mainPageThxNotification}`
  }

  return url
}

type BeginValues = {
  plan: string
  paymentType: string
  paymentMethod: string
  billing: ShippingType
  shipping: ShippingType
  proceedWithPrepaidCard?: boolean
  amazonPayBillingAgreementId?: string
}

type BuySubscriptionValues = BeginValues & {
  creditCard: CreditCardType & {
    cvv: string
  },
  isEnoughCreditsToBuy: boolean
  paymentType: string
  isNewSubscribeFlowEnabled?: boolean
}

const checkBeginValues = (values) => {
  const error = checkRequiredFields(Object.keys(values), [
    'shippingFirstName',
    'shippingLastName',
    'shippingStreet',
    'shippingApt',
    'shippingZipCode',
    'shippingState',
    'shippingCity',
    'billingFirstName',
    'billingLastName',
    'billingStreet',
    'billingApt',
    'billingZipCode',
    'billingState',
    'billingCity',
    'plan',
    'paymentType',
    'ignoreCredits',
  ])

  if (error) {
    sentry.captureMessage(error, {
      extra: {
        values,
      },
    })
  }
}

const begin = (values: BeginValues) => {
  const {
    models: { me: { needUpdateGender, gender } },
    view: { paymentPage: { useCredits } },
  } = getState()

  const { shipping, billing, paymentMethod, plan } = values

  const body: any = {
    ...getPrefixedAddressFields({ shipping, billing }),
    plan,
    paymentType: paymentMethod, // TODO ask backend rename `paymentType` to `paymentMethod`
    ignoreCredits: !useCredits,
    amazonPayBillingAgreementId: values.amazonPayBillingAgreementId || null, // for amazon pay only
  }

  if (values.proceedWithPrepaidCard) {
    body.proceedWithPrepaidCard = true
  }

  checkBeginValues(body)

  const begin = setupBegin({
    endpoint: 'rest/subscription/begin',
    tokenName: constants.requestTokens.subscription,
    withSubscriptionCase: true,
  })

  if (needUpdateGender) {
    return actions.models.me.update({ gender })
      .then(() => begin(body))
  }

  return begin(body)
}


type CommitValues = {
  subscriptionSession: string
  token: string
}

const commit = (values: CommitValues) => {
  const { view: { paymentPage: { useCredits } } } = getState()

  const body = {
    ...values,
    ignoreCredits: !useCredits,
  }

  const commit = setupCommit({
    endpoint: 'rest/subscription/commit',
    tokenName: constants.requestTokens.subscription,
    withErrorModal: false,
    withProgressRequest: getFeatureToggle(constants.features.newSubscriptionPaymentProcess),
  })

  return commit(body)
}


type ErrorArg = {
  title: string
  message: string
  statusCode: number
  resData: {
    authCode: string
    isCvvError: boolean
  }
}

const showBuySubscriptionErrorModal = (err: ErrorArg, values: BuySubscriptionValues) => {
  const { title, message, statusCode, resData: { authCode: authCodeString, isCvvError } } = err

  // Error codes https://github.com/scentbird/scnt-payment-gateway/blob/master/src/main/groovy/com/scentbird/gateway/vindicia/AuthCode.groovy
  const authCode            = parseInt(authCodeString, 10)
  const isBillingError      = authCode === 713
  const isPrepaidCardError  = authCode === 309

  const isPopupWithDiscountEnabled = (
    isPrepaidCardError
    && getFeatureToggle(constants.features.cardFailedPopupWithDiscount)
  )

  const isPopupWithSubscriptionPlansEnabled = (
    isPrepaidCardError
    && !getCookieValue(constants.cookieNames.isPrepaidCardWithSubscriptionPlansModalHidden)
  )

  actions.view.paymentPage.setBillingError(isBillingError)

  if (isCvvError) {
    actions.models.modals.open(constants.modals.ErrorCVV, {
      onSubmit: ({ cvv }) => buySubscription({ ...values, creditCard: { ...values.creditCard, cvv } }),
    })
  }
  else if (isPopupWithDiscountEnabled) {
    const isSpecialFlow = Boolean(getCookieValue(constants.cookieNames.specialFlowName))

    actions.models.modals.open(constants.modals.prepaidCard.PrepaidCardWithDiscount, {
      // Hide ProceedButton in PrepaidCardWithDiscountModal. (only for special flow)
      handleProceed: !isSpecialFlow && (
        () => buySubscription({ ...values, proceedWithPrepaidCard: true })
      ),
      inquireValues: values.shipping,
    })
  }
  else if (isPopupWithSubscriptionPlansEnabled) {
    actions.models.modals.open(constants.modals.prepaidCard.PrepaidCardWithSubscriptionPlans, {
      onChoose: (plan) => {
        actions.view.paymentPage.setSelectedPlan(plan)
        buySubscription({ ...values, plan })
      },
    })
  }
  else if (isBillingError) {
    if (values.isNewSubscribeFlowEnabled) {
      actions.models.router.push(links.subscribe)
    }

    actions.models.modals.open(constants.modals.ServerError, {
      title,
      content: message,
      statusCode,
      buttonTitle: 'Re-enter info',
    })
  }
  else {
    console.log({ title, content: message, statusCode })
    actions.models.modals.open(constants.modals.ErrorCard)
  }
}


const buySubscription = (values: BuySubscriptionValues) => {
  const { shipping, billing, creditCard, paymentType, isEnoughCreditsToBuy } = values

  mixpanel.track(constants.analyticsEvents.tech.buySubscription)

  actions.models.payment.update({ isSubscriptionFetching: true })
  actions.models.payment.setUserShippingAddress(values.shipping)

  return begin(values)
    .then(
      (data) => {
        const body = {
          paymentType,
          shipping,
          billing,
          creditCard,
          ...data,
        }

        if (isEnoughCreditsToBuy) {
          return Promise.resolve(data)
        }

        return callVindicia(body)
      },
      (err) => Promise.reject(err)
    )
    .then(
      ({ subscriptionSession, token }) => commit({ subscriptionSession, token }),
      (err) => Promise.reject(err)
    )
    .then(
      (result) => {
        mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
          success: true,
        })

        return syncUserAndSubscription({ paymentType, ...result })
      },
      (err) => {
        const { title, message, resData: { authCode } = { authCode: undefined } } = err

        showBuySubscriptionErrorModal(err, values)
        mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
          success: false,
          reason: `${authCode} | ${title} | ${message}`,
        })

        return Promise.reject(err)
      }
    )
    .finally(() => {
      actions.models.payment.update({ isSubscriptionFetching: false })
    })
    .then(() => {
      const {
        view: { postponedPremiumProduct: { product } },
        models: { me: { gender } },
      } = getState()

      clearResolvedProducts()
      actions.view.paymentPage.setBillingError(false)
      actions.misc.cookies.removeItem(constants.cookieNames.selectedPlan)

      if (product) {
        actions.models.queue.addItem(product, { placement: 'buy subscription premium product' })
      }

      callAnalytics()
      const url = getNextPageUrl(gender)
      actions.models.router.push(url)
      removeSubscriptionCase()
    })
}


// PayPal

const beginSubscriptionUsingPayPal = (values: BeginValues) => {
  const beginPayPal = setupBeginPayPal({
    begin,
    sessionName: 'subscriptionSession',
  })

  actions.models.payment.setUserShippingAddress(values.shipping)

  return beginPayPal(values)
}


type CommitPayPalValues = {
  paymentType: string
  paypalSessionId: string
}

const commitSubscriptionUsingPayPal = (values: CommitPayPalValues) => {
  const commitPayPal = setupCommitPayPal({
    commit,
    sessionName: 'subscriptionSession',
  })

  return commitPayPal(values)
    .then((data) => {
      clearResolvedProducts()
      removeSubscriptionCase()

      return data
    })
}


// ApplePay

type BeginApplePayValues = BeginValues & {
  applePayValidationURL: string
}

const beginSubscriptionUsingApplePay = (values: BeginApplePayValues) => {
  const { models: { me: { needUpdateGender, gender } } } = getState()

  const begin = setupBegin({
    endpoint: 'rest/subscription/begin',
    tokenName: constants.requestTokens.subscription,
    withSubscriptionCase: true,
  })

  const beginApplePay = setupBeginApplePay({
    begin,
    sessionName: 'subscriptionSession',
  })

  actions.models.payment.setUserShippingAddress(values.shipping)

  if (needUpdateGender) {
    return actions.models.me.update({ gender })
      .then(() => beginApplePay({ ...values }))
  }

  return beginApplePay({ ...values })
}


type sessionArg = {
  completePayment: (any) => any
}

const commitSubscriptionUsingApplePay = (session: sessionArg, values: { [key: string]: string }) => {
  const commitApplePay = setupCommitApplePay({
    commit,
    session,
    sessionName: 'subscriptionSession',
    paymentType: constants.paymentTypes.subscription,
  })

  return commitApplePay(values)
    .then(
      () => {
        const { models: { me: { gender } } } = getState()

        clearResolvedProducts()
        callAnalytics()
        const url = getNextPageUrl(gender)
        actions.models.router.push(url)
        removeSubscriptionCase()
      },
      (err) => Promise.reject(err)
    )
}


// AmazonPay

const buySubscriptionUsingAmazonPay = (values: BeginValues) => {
  actions.models.payment.setUserShippingAddress(values.shipping)

  return begin(values)
    .then(
      ({ subscriptionSession, token }) => commit({ subscriptionSession, token }),
      (err) => Promise.reject(err)
    )
    .then(
      (result) => syncUserAndSubscription({
        paymentType: values.paymentType,
        ...result,
      }),
      ({ title, message, statusCode }) => {
        actions.models.modals.open(constants.modals.ServerError, { title, content: message, statusCode })

        return Promise.reject(message)
      }
    )
    .then(() => {
      const {
        view: { postponedPremiumProduct: { product } },
        models: { me: { gender } },
      } = getState()

      clearResolvedProducts()
      actions.misc.cookies.removeItem(constants.cookieNames.selectedPlan)

      if (product) {
        actions.models.queue.addItem(product, { placement: 'buy subscription premium product' })
      }

      callAnalytics()
      const url = getNextPageUrl(gender)
      actions.models.router.push(url)
      removeSubscriptionCase()
    })
}

const getAmazonUserProfile = ({ accessToken, amazonPayBillingAgreementId }) => (
  request.post('graphql?name=amazonUserProfileQuery', {
    body: {
      query: amazonUserProfileQuery,
      variables: {
        accessToken,
        amazonPayBillingAgreementId,
      },
    },
    withNestedErrors: true,
    modifyResult: ({ amazonUserProfile: { shippingAddress, result } }) => {
      if (result) {
        const { telephone, ...address } = shippingAddress

        const isPhoneWithPrefix = /^\+1/.test(telephone)
        const phoneWithPrefix   = isPhoneWithPrefix ? telephone : `+1${telephone}`
        const isValidPhone      = /^\+1\d{10}$/.test(phoneWithPrefix)
        const updatedTelephone  = isValidPhone
          ? phoneWithPrefix.replace(/^\+1(\d{3})(\d{3})(\d{2})(\d{2})/, '+1 $1 $2 $3 $4')
          : ''

        if (phoneWithPrefix && !isValidPhone) {
          sentry.captureException('Wrong phone received from amazon pay', {
            extra: {
              telephone,
            },
          })
        }

        return {
          shippingAddress: {
            telephone: updatedTelephone,
            ...address,
          },
        }
      }
    },
  })
    .then(
      ({ shippingAddress }) => {
        if (shippingAddress) {
          return shippingAddress
        }
      }
    )
)

const postponedSubscriptionProcessing = () => {
  const {
    models: {
      payment: { subscriptionProgress },
      me: { gender },
    },
  } = getState()

  if (subscriptionProgress) {
    const { status, subscriptionSessionId } = subscriptionProgress

    if (!subscriptionSessionId || status !== constants.subscriptionProcessingStatuses.progress) {
      return
    }

    mixpanel.track(constants.analyticsEvents.tech.postponedSubscriptionProcessing)

    actions.models.payment.update({
      isSubscriptionFetching: true,
      instantShowDisplayProcessBanner: true,
    })

    subscriptionProcessing({ subscriptionSession: subscriptionSessionId })
      .then(
        (result) => {
          syncUserAndSubscription({
            paymentType: constants.paymentTypes.subscription,
            ...result,
          })

          clearResolvedProducts()
          actions.misc.cookies.removeItem(constants.cookieNames.selectedPlan)

          callAnalytics()

          const url = getNextPageUrl(gender)
          actions.models.router.push(url)
        },
        ({ title, message, statusCode }) => {
          actions.models.modals.open(constants.modals.ServerError, { title, content: message, statusCode })
        }
      )
      .finally(() => {
        actions.models.payment.update({
          isSubscriptionFetching: false,
          instantShowDisplayProcessBanner: false,
        })
      })
  }
}

const clearResolvedProducts = () => {
  const { view: { pickPage: { selectedItems } } } = getState()

  const isPremiumProductsForNotSubscribedEnabled = getFeatureToggle(constants.features.premiumProductsForNotSubscribed)

  if (isPremiumProductsForNotSubscribedEnabled && selectedItems.length) {
    actions.view.pickPage.clearSelectedItems()
  }

  actions.models.cart.clear()
}


export {
  buySubscription,
  begin,
  commit,
  beginSubscriptionUsingPayPal,
  commitSubscriptionUsingPayPal,
  beginSubscriptionUsingApplePay,
  commitSubscriptionUsingApplePay,
  postponedSubscriptionProcessing,
  buySubscriptionUsingAmazonPay,
  getAmazonUserProfile,
}
